import React from "react";
import { useTranslation } from "react-i18next";
import {
  faCalendarCheck,
  faLock,
  faCheck,
  faArrowLeft,
  faArrowRight,
  faForward,
  faUserSlash,
  faBan,
  faHourglass,
} from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export const getBookingKind = (booking) => {
  const rawKind =
    booking?.bookingType ??
    booking?.type ??
    booking?.bookingKind ??
    booking?.status ??
    "BOOKING";

  return String(rawKind).toUpperCase();
};

const RESERVATION_STATUS_BY_NUMBER = {
  0: "PENDING",
  1: "RESERVED",
  2: "CANCELED",
  3: "FAILED",
  4: "NO_SHOW",
  5: "FULFILLED",
  6: "REJECTED",
};

const CANCELED_OR_NO_SHOW_STATUSES = new Set(["CANCELED", "CANCELLED", "NO_SHOW"]);

export const getReservationStatusKey = (booking) => {
  const raw = booking?.reservationStatus ?? booking?.ReservationStatus ?? "";

  if (typeof raw === "number") {
    return RESERVATION_STATUS_BY_NUMBER[raw] ?? String(raw);
  }

  return String(raw).trim().toUpperCase().replace(/[\s-]+/g, "_");
};

export const isCanceledOrNoShowBooking = (booking) =>
  CANCELED_OR_NO_SHOW_STATUSES.has(getReservationStatusKey(booking));

export const isTimelineBookingWithoutBuffer = (booking) => isCanceledOrNoShowBooking(booking);

const getIsChargerNotified = (booking) =>
  booking?.isChargerNotified;

export const BOOKING_APPEARANCE = {
  BOOKING: {
    id: "BOOKING",
    label: "Booking",
    description: "Normal reservation",
    shortLabel: "Booking",
    className:
      "bg-primary-100 text-primary-500 border-primary-400",
    accentClassName: "bg-primary-500",
    iconWrapClassName: "bg-primary-300 text-primary-500",
    icon: faCalendarCheck,
  },
  CHARGER_NOTIFIED: {
    id: "CHARGER_NOTIFIED",
    label: "Confirmed reservation",
    description: "Sent to charger",
    shortLabel: "Confirmed",
    className:
      "bg-energi-200 text-primary-500 border-energi-500",
    accentClassName: "bg-energi-500",
    iconWrapClassName: "bg-energi-400 text-primary-500",
    icon: faCheck,
  },
  OCCUPIED: {
    id: "OCCUPIED",
    label: "Occupied",
    description: "Booked by someone else",
    shortLabel: "Busy",
    className: "bg-mute-200 text-primary-500 border-mute-400",
    accentClassName: "bg-mute-500",
    iconWrapClassName: "bg-mute-300 text-primary-500",
    icon: faUserSlash,
  },
  UNAVAILABILITY: {
    id: "UNAVAILABILITY",
    label: "Unavailability",
    description: "Unavailable period",
    shortLabel: "Blocked",
    className: "bg-mute-300 text-primary-500 border-mute-500",
    accentClassName: "bg-mute-700",
    iconWrapClassName: "bg-mute-400 text-primary-500",
    icon: faLock,
  },
  CANCELED: {
    id: "CANCELED",
    label: "Canceled",
    description: "Canceled reservation",
    shortLabel: "Canceled",
    className: "bg-mute-100 text-mute-700 border-mute-400",
    accentClassName: "bg-mute-500",
    iconWrapClassName: "bg-mute-200 text-mute-700",
    icon: faBan,
  },
  NO_SHOW: {
    id: "NO_SHOW",
    label: "No show",
    description: "Customer did not arrive",
    shortLabel: "No show",
    className: "bg-kraft-100 text-kraft-700 border-kraft-500",
    accentClassName: "bg-kraft-500",
    iconWrapClassName: "bg-kraft-200 text-kraft-700",
    icon: faHourglass,
  },
  BUFFER_BEFORE: {
    id: "BUFFER_BEFORE",
    label: "Buffer",
    description: "Early start",
    shortDescription: "Early",
    className: "bg-kraft-200 text-primary-500 border-kraft-500",
    accentClassName: "bg-kraft-500",
    iconWrapClassName: "bg-kraft-300 text-primary-500",
    icon: faArrowLeft,
  },
  BUFFER_AFTER: {
    id: "BUFFER_AFTER",
    label: "Buffer",
    description: "Late stop",
    shortDescription: "Late stop",
    className: "bg-kraft-200 text-primary-500 border-kraft-500",
    accentClassName: "bg-kraft-500",
    iconWrapClassName: "bg-kraft-300 text-primary-500",
    icon: faArrowRight,
  },
  BUFFER_NEXT_START: {
    id: "BUFFER_NEXT_START",
    label: "Next start",
    description: "Possible next start",
    shortDescription: "Next",
    className: "bg-volt-100 text-primary-500 border-volt-400",
    accentClassName: "bg-volt-500",
    iconWrapClassName: "bg-volt-200 text-primary-500",
    icon: faForward,
  },
};

export const getAppearanceText = (appearance, t, field = "label") => {
  if (!appearance) {
    return "";
  }

  const id = appearance.id;
  if (!id || !t) {
    return appearance[field] || appearance.label || "";
  }

  const key = `booking.legend.${id}.${field}`;
  const translated = t(key);
  if (translated === key) {
    return appearance[field] || appearance.label || "";
  }

  return translated;
};

export const getBookingAppearance = (booking) => {
  if (booking?.isOwn === false) {
    return BOOKING_APPEARANCE.OCCUPIED;
  }

  const status = getReservationStatusKey(booking);

  if (status === "CANCELED" || status === "CANCELLED") {
    return BOOKING_APPEARANCE.CANCELED;
  }

  if (status === "NO_SHOW") {
    return BOOKING_APPEARANCE.NO_SHOW;
  }

  const kind = getBookingKind(booking);

  if (getIsChargerNotified(booking) === true) {
    return BOOKING_APPEARANCE.CHARGER_NOTIFIED;
  }

  if (kind.includes("UNAVAILABILITY")) {
    return BOOKING_APPEARANCE.UNAVAILABILITY;
  }

  return BOOKING_APPEARANCE.BOOKING;
};

const getLegendItems = (showCanceledAndNoShow) => [
  BOOKING_APPEARANCE.BOOKING,
  BOOKING_APPEARANCE.CHARGER_NOTIFIED,
  BOOKING_APPEARANCE.OCCUPIED,
  BOOKING_APPEARANCE.UNAVAILABILITY,
  ...(showCanceledAndNoShow
    ? [BOOKING_APPEARANCE.CANCELED, BOOKING_APPEARANCE.NO_SHOW]
    : []),
  BOOKING_APPEARANCE.BUFFER_BEFORE,
  BOOKING_APPEARANCE.BUFFER_AFTER,
  BOOKING_APPEARANCE.BUFFER_NEXT_START,
];

const BookingLegend = ({ showCanceledAndNoShow = false }) => {
  const { t } = useTranslation();
  const legendItems = getLegendItems(showCanceledAndNoShow);

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
      {legendItems.map((item) => (
        <div
          key={item.id || `${item.label}-${item.description}`}
          className={`inline-flex min-w-0 items-center gap-1.5 rounded-lg border px-2.5 py-1 text-xs shadow-sm ${item.className}`}
        >
          <span
            className={`inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full ${item.iconWrapClassName}`}
          >
            <FontAwesomeIcon icon={item.icon} className="text-[9px]" />
          </span>
          <span className="min-w-0 truncate leading-snug">
            <span className="font-semibold">{t(`booking.legend.${item.id}.label`)}</span>
            <span className="opacity-80"> · {t(`booking.legend.${item.id}.description`)}</span>
          </span>
        </div>
      ))}
    </div>
  );
};

export default BookingLegend;
